import { ACADEMIC_STATUS_MAP } from 'constants/academic-status-map';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { Divider, Typography } from 'antd';
import { IAcademicRecord } from 'interfaces/academic_record';
import { ReactNode } from 'react';
import { formatDate } from 'services/date';

interface AcademicRecordInfoProps {
  record: IAcademicRecord;
  kindLabel?: string;
}

const STATUS_CARD_MAP: Record<
  IAcademicRecord['status'],
  {
    description: string;
    cardClassName: string;
    titleClassName: string;
    icon: typeof CheckCircleOutlined;
    iconClassName: string;
  }
> = {
  approved: {
    description: 'Critérios de aprovação atendidos.',
    cardClassName: 'border-emerald-200 bg-emerald-50',
    titleClassName: 'text-emerald-800',
    icon: CheckCircleOutlined,
    iconClassName: 'text-emerald-600'
  },
  failed: {
    description: 'Nota insuficiente para aprovação.',
    cardClassName: 'border-rose-200 bg-rose-50',
    titleClassName: 'text-rose-800',
    icon: CloseCircleOutlined,
    iconClassName: 'text-rose-600'
  },
  attendance_failed: {
    description: 'Frequência mínima não atingida.',
    cardClassName: 'border-rose-200 bg-rose-50',
    titleClassName: 'text-rose-800',
    icon: CloseCircleOutlined,
    iconClassName: 'text-rose-600'
  },
  exempted: {
    description: 'Dispensado da disciplina por equivalência.',
    cardClassName: 'border-neutral-200 bg-neutral-50',
    titleClassName: 'text-neutral-800',
    icon: MinusCircleOutlined,
    iconClassName: 'text-neutral-500'
  }
};

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <Typography.Text strong className="text-sm text-neutral-800">
      {label}
    </Typography.Text>
    <Typography.Text className="text-neutral-700">
      {value ?? '-'}
    </Typography.Text>
  </div>
);

const MetricCard = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="h-full rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
    <Typography.Text className="text-sm text-neutral-700">
      {label}
    </Typography.Text>
    <div className="mt-1 text-xl font-semibold text-neutral-800">{value}</div>
  </div>
);

const StatusCard = ({ status }: { status: IAcademicRecord['status'] }) => {
  const meta = ACADEMIC_STATUS_MAP[status];
  const style = STATUS_CARD_MAP[status];
  const Icon = style.icon;

  return (
    <div
      className={`h-full rounded-lg border px-4 py-3 ${style.cardClassName}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`mt-0.5 shrink-0 text-xl ${style.iconClassName}`} />
        <div className="min-w-0">
          <span
            className={`block text-xl font-semibold leading-tight ${style.titleClassName}`}
          >
            {meta.label}
          </span>
          <Typography.Text type="secondary" className="mt-0.5 block text-sm">
            {style.description}
          </Typography.Text>
        </div>
      </div>
    </div>
  );
};

const formatScore = (score: IAcademicRecord['score']) =>
  score == null ? '--' : score;

const formatAttendance = (attendanceRate: IAcademicRecord['attendance_rate']) =>
  attendanceRate == null ? 'Sem frequência' : `${attendanceRate}%`;

export const AcademicRecordInfo = ({
  record,
  kindLabel
}: AcademicRecordInfoProps) => {
  const isClassroom =
    record.parent_type === 'Classrooms::Enrollment' &&
    Boolean(record.parent) &&
    'classroom' in record.parent;
  const classroom = isClassroom ? record.parent.classroom : undefined;
  const professorName =
    classroom?.classroom_professors?.[0]?.professor?.name ?? '-';
  const validity =
    classroom?.starts_at && classroom?.ends_at
      ? `${formatDate(classroom.starts_at, 'DD/MM/YYYY')} – ${formatDate(
          classroom.ends_at,
          'DD/MM/YYYY'
        )}`
      : '-';

  return (
    <div>
      <Typography.Title level={5} className="!mb-0">
        Informações acadêmicas
      </Typography.Title>

      <Divider className="!my-4" />

      <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
        <InfoField
          label="Curso"
          value={record.course_enrollment?.course?.name ?? '-'}
        />
        <InfoField
          label="Aluno"
          value={record.course_enrollment?.user?.full_name_or_name ?? '-'}
        />
        <InfoField label="Disciplina" value={record.discipline?.name ?? '-'} />
        <InfoField label="Tipo" value={kindLabel ?? '-'} />
        {isClassroom && (
          <>
            <InfoField label="Turma" value={classroom?.code ?? '-'} />
            <InfoField label="Professor" value={professorName} />
            <InfoField label="Vigência" value={validity} />
          </>
        )}
      </div>

      <Divider className="!my-4" />

      <Typography.Title level={5} className="!mb-3">
        Desempenho
      </Typography.Title>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatusCard status={record.status} />
        <MetricCard label="Nota" value={formatScore(record.score)} />
        <MetricCard
          label="Frequência"
          value={formatAttendance(record.attendance_rate)}
        />
      </div>
    </div>
  );
};
